import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { logout } from "../../../services/userService";
import { useUser } from "../../../context/UserContext";
import { useOrganization } from "../../../context/OrganizationContext";
import SidebarLeft from "../../_asset/img/sidebar-left.svg";
import Logo from "../../common/Logo";

// Material-UI Outlined Icons
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AlarmAddIcon from '@mui/icons-material/AlarmAdd';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';

const Sidebar = ({ isOpen }) => {
  const location = useLocation();
  const { userInfo } = useUser();
  const { organizationData } = useOrganization();
  const organizationId = useParams().organizationId;
  const [sidebarOpen, setSidebarOpen] = useState(isOpen || true); // Default to collapsed (true = collapsed)

  const isActive = (path) => location.pathname.includes(path);

  const initiateLogout = async (e) => {
    e.preventDefault();
    try {
      const resp = await logout();
      window.location.href = resp.data.logoutUrl;
    } catch (error) {
      // If logout API fails, redirect to login page as fallback
      window.location.href = '/login';
    }
  };

  const getOrganizationDetails = () => {
    if (!organizationData) return { name: "Loading...", avatarInitial: "L", fullName: "Loading..." };

    const orgName = organizationData.name || organizationData.agency_name || organizationData.organization_name || "Unknown Organization";
    const avatarInitial = orgName.charAt(0).toUpperCase();
    
    // Truncate name if too long (adjust max length as needed)
    const maxLength = 18;
    const truncatedName = orgName.length > maxLength 
      ? `${orgName.substring(0, maxLength)}...` 
      : orgName;

    return { 
      name: truncatedName, 
      fullName: orgName,
      avatarInitial 
    };
  };

  const { name, fullName, avatarInitial } = getOrganizationDetails();

  return (
    <div className={`${
      sidebarOpen 
        ? "w-[89px] bg-[#232639]" 
        : "w-[272px] bg-white"
    } h-screen shadow-[2px_0_5px_rgba(31,10,10,0.1)] flex flex-col font-inter border-r border-[#E4E4E4] transition-all duration-300 relative`}>
      <div className="p-6 border-b border-[#E4E4E4] h-[94px] flex items-center justify-between">
        {sidebarOpen ? (
          // Collapsed State - Show logo bars with expand functionality
          <div 
            className="flex justify-center items-center w-full cursor-pointer transition-all duration-300"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            title="Expand sidebar"
          >
            <Logo showText={false} showBars={true} size="default" />
          </div>
        ) : (
          // Expanded State - Show full user info and organization switching
          <>
            <Link 
              to="/home" 
              className="flex items-center gap-3 flex-1 no-underline transition-all duration-300"
            >
              <div className="w-10 h-10 flex items-center justify-center">
                <Logo showText={false} showBars={true} size="default" />
              </div>
              <div className="flex flex-col justify-center">
                <p className="font-semibold m-0 text-black text-sm leading-tight">{userInfo.firstName} {userInfo.lastName}</p>
                <span className="text-xs font-normal text-[#878787] leading-tight">
                  {name}
                </span>
                <span className="text-[10px] font-normal text-[#B8B8B8] leading-tight mt-0.5 italic">
                  Switch organization
                </span>
              </div>
            </Link>
            
            {/* Collapse Button */}
            <div 
              className="w-6 h-6 flex items-center justify-center bg-transparent cursor-pointer transition-all duration-300 hover:bg-blue-50 rounded ml-2"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              <img 
                src={SidebarLeft} 
                alt="" 
                className="w-4 h-4 transition-transform duration-300" 
              />
            </div>
          </>
        )}
      </div>

      <div className="p-6 h-[calc(100vh-94px)] flex flex-col justify-between overflow-y-auto" id="scrollBar">
        <div className="mb-6">
          <h4 className={`text-xs font-medium mb-2 text-[#878787] transition-all duration-300 ${sidebarOpen ? 'hidden' : 'block'}`}>MAIN MENU</h4>
          <nav className="flex flex-col gap-2">
            <Link
              to={`/organization/${organizationId}/threads`}
              className={`text-gray-600 p-3 rounded-lg flex items-center text-sm gap-2 font-medium no-underline transition-all duration-300 hover:bg-blue-50 hover:text-blue-600 ${
                sidebarOpen ? 'p-2.5 justify-center' : ''
              } ${
                isActive("threads") 
                  ? "bg-gray-100 text-primary" 
                  : ""
              }`}
              title={sidebarOpen ? "Threads" : ""}
            >
              <AutoAwesomeIcon 
                sx={{ 
                  fontSize: 20,
                  color: isActive("threads") ? 'var(--primary-color, #3b82f6)' : 'inherit'
                }} 
              />
              <span className={`transition-all duration-300 ${sidebarOpen ? 'hidden' : 'block'}`}>Threads</span>
            </Link>
            <Link
              to={`/organization/${organizationId}/analyst`}
              className={`text-gray-600 p-3 rounded-lg flex items-center text-sm gap-2 font-medium no-underline transition-all duration-300 hover:bg-blue-50 hover:text-blue-600 ${
                sidebarOpen ? 'p-2.5 justify-center' : ''
              } ${
                isActive("analyst") 
                  ? "bg-gray-100 text-primary" 
                  : ""
              }`}
              title={sidebarOpen ? "Analyst" : ""}
            >
              <AssessmentIcon 
                sx={{ 
                  fontSize: 20,
                  color: isActive("analyst") ? 'var(--primary-color, #3b82f6)' : 'inherit'
                }} 
              />
              <span className={`transition-all duration-300 ${sidebarOpen ? 'hidden' : 'block'}`}>Analyst</span>
            </Link>
            <Link
              to={`/organization/${organizationId}/alerts`}
              className={`text-gray-600 p-3 rounded-lg flex items-center text-sm gap-2 font-medium no-underline transition-all duration-300 hover:bg-blue-50 hover:text-blue-600 ${
                sidebarOpen ? 'p-2.5 justify-center' : ''
              } ${
                isActive("alerts") 
                  ? "bg-gray-100 text-primary" 
                  : ""
              }`}
              title={sidebarOpen ? "Alerts" : ""}
            >
              <AlarmAddIcon 
                sx={{ 
                  fontSize: 20,
                  color: isActive("alerts") ? 'var(--primary-color, #3b82f6)' : 'inherit'
                }} 
              />
              <span className={`transition-all duration-300 ${sidebarOpen ? 'hidden' : 'block'}`}>Alerts</span>
            </Link>
          </nav>
        </div>

        <div>
          <h4 className={`text-xs font-medium mb-2 text-gray-500 transition-all duration-300 ${sidebarOpen ? 'hidden' : 'block'}`}>OTHERS</h4>
          <nav className="flex flex-col gap-2">
            <Link
              to={`/organization/${organizationId}/settings`}
              className={`text-gray-600 p-3 rounded-lg flex items-center text-sm gap-2 font-medium no-underline transition-all duration-300 hover:bg-blue-50 hover:text-blue-600 ${
                sidebarOpen ? 'p-2.5 justify-center' : ''
              } ${
                isActive("settings") 
                  ? "bg-gray-100 text-primary" 
                  : ""
              }`}
              title={sidebarOpen ? "Account" : ""}
            >
              <SettingsOutlinedIcon 
                sx={{ 
                  fontSize: 20,
                  color: isActive("settings") ? 'var(--primary-color, #3b82f6)' : 'inherit'
                }} 
              />
              <span className={`transition-all duration-300 ${sidebarOpen ? 'hidden' : 'block'}`}>Account</span>
            </Link>
            <Link
              to={`/`}
              onClick={initiateLogout}
              className={`text-red-500 p-3 rounded-lg flex items-center text-sm gap-2 font-medium no-underline transition-all duration-300 hover:text-red-600 ${
                sidebarOpen ? 'p-2.5 justify-center' : ''
              } ${
                isActive("logout") 
                  ? "bg-gray-100" 
                  : ""
              }`}
              title={sidebarOpen ? "Logout" : ""}
            >
              <LogoutOutlinedIcon 
                sx={{ 
                  fontSize: 20,
                  color: '#ef4444'
                }} 
              />
              <span className={`transition-all duration-300 ${sidebarOpen ? 'hidden' : 'block'}`}>Logout</span>
            </Link>
          </nav>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
